import React, { useCallback, useState, useContext, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { useAuthentication } from 'gw-digital-auth-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { ServiceManager } from '@jutro/services';
import metadata from './EnrollmentComponent.metadata.json5';
import styles from './EnrollmentComponent.module.scss';
import { PitneyBowesService } from '../../../../capabilities/nexus-capability-common';

import messages from './EnrollmentComponent.messages';

const ENROLLMENT_ERRORS = {
    enrollmentInvalid: 'enrollmentInvalid',
    enrollmentFailed: 'enrollmentFailed',
    fieldEmpty: 'fieldEmpty',
    denyPrimaryAddress: 'denyPrimaryAddress',
    denyPolicyNotFound: 'denyPolicyNotFound'
};

function EnrollmentComponent(props) {
    const translator = useContext(TranslatorContext);
    const [formData, updateFormData] = useState({ addressLine1: '', postalcode: '' });
    const [enrollmentStatus, setEnrollmentStatus] = useState();
    const [availablePolicies, setAvailablePolicies] = useState([]);
    const { authHeader, refreshToken } = useAuthentication();
    const { EnrollmentService } = useDependencies('EnrollmentService');
    const [pitneyBowesInstanceKey, setPitneyBowesInstanceKey] = useState();
    const localeService = ServiceManager.getService('locale-service');
    const [availableAddresses, setAvailableAddresses] = useState('');

    const {
        enrollmentType,
        onSuccessEnrollment,
        informationText,
        informationAdditionalText,
        onReturnLinkClick,
        returnLinkText,
        enrollButtonText
    } = props;

    useEffect(() => {
        PitneyBowesService.createAddressingInstanceKey().then(data => {
            setPitneyBowesInstanceKey(data);
        });
    }, []);

    const enrollPolicy = useCallback(
        (evt) => {
            if (evt) {
                evt.preventDefault();
            }

            const { policyNumber, postalcode } = formData;

            if (_.isEmpty(policyNumber)  || _.isEmpty(postalcode)) {
                setEnrollmentStatus(ENROLLMENT_ERRORS.fieldEmpty);
                return Promise.resolve();
            }

            return EnrollmentService.addEnrollmentRecord(
                {
                    details: {
                        policyNumber: policyNumber,
                       // addressLine1: addressLine1,
                        postalcode: postalcode
                    },
                    type: enrollmentType
                },
                authHeader
            )
                .then((resp) => {
                    refreshToken().then(() => {
                        if (onSuccessEnrollment) {
                            onSuccessEnrollment();
                        } else {
                            updateFormData({});
                            setAvailablePolicies(resp);
                            setEnrollmentStatus('enrollmentRequestSucceeded');
                        }
                    });
                })
                .catch((error) => {
                    setAvailablePolicies([]);
                    if (error && error.baseError) {
                        if (error.baseError.includes('Primary address')) {
                            setEnrollmentStatus(ENROLLMENT_ERRORS.denyPrimaryAddress);
                        } else if (error.baseError.includes('policy number')) {
                            setEnrollmentStatus(ENROLLMENT_ERRORS.denyPolicyNotFound);
                        } else {
                            setEnrollmentStatus(ENROLLMENT_ERRORS.enrollmentInvalid);
                        }
                    } else {
                        setEnrollmentStatus(ENROLLMENT_ERRORS.enrollmentFailed);
                    }
                });
        },
        [EnrollmentService, authHeader, enrollmentType, formData, onSuccessEnrollment, refreshToken]
    );

    const writeValue = useCallback(
        (value, path) => {
            const nextFormData = _.cloneDeep(formData);
            _.set(nextFormData, path, value);
            updateFormData(nextFormData);
        },
        [formData]
    );

    const getSuccessNotificationMessage = useCallback(() => {
        return (
            <div>
                <span>
                    {availablePolicies.length > 1
                        ? translator(messages.accessToYourPolicies)
                        : translator(messages.accessToYourPolicy)}
                </span>
                <span>{availablePolicies.join(', ')}</span>
            </div>
        );
    }, [availablePolicies, translator]);

    const getAddressesFromTheApi = async (textInput) => {
        let dataArray = [];
        if (textInput === undefined ) {
            return dataArray;
        }
        const { userState } = formData;
        const response = await PitneyBowesService.globalTypeAheadCANRequest(pitneyBowesInstanceKey, textInput, userState);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const responseJson = await response.json();
        const data = responseJson.Output;
        if (data) {
            const originalAddresses = [];
            data.forEach((address, index) => {
                dataArray = [...dataArray, {id: index, displayName: `${address.AddressLine1}, ${address.City}, ${address.PostalCode}, ${address.StateProvince}`, value: address.AddressLine1}];
                originalAddresses[index] = address;
            });
            setAvailableAddresses(originalAddresses);
        }
        return dataArray;
    }


    const handleValueAddressLookup = (value) => {
        if (value) {
            const address = availableAddresses[value.id];
            /* writeValue(address.PostalCode, 'postalcode');
            writeValue(address.AddressLine1, 'addressLine1'); */
            const nextFormData = _.cloneDeep(formData);
            _.set(nextFormData, 'postalcode', address.PostalCode);
            _.set(nextFormData, 'addressLine1', address.AddressLine1);
            updateFormData(nextFormData);
        }
    };

    const overrideProps = {
        verificationInformationText: {
            content: translator(informationText)
        },
        verificationInformationAdditionalText: {
            content: translator(informationAdditionalText)
        },
        enrollmentErrorMessageContainer: {
            visible:
                !_.isUndefined(enrollmentStatus)
                && enrollmentStatus !== 'enrollmentRequestSucceeded'
        },
        enrollmentErrorMessage: {
            message: translator(messages[enrollmentStatus])
        },
        enrollmentSuccessNotificationContainer: {
            visible: enrollmentStatus === 'enrollmentRequestSucceeded'
        },
        enrollmentSuccessNotification: {
            message: availablePolicies.length !== 0 ? getSuccessNotificationMessage() : ''
        },
        returnLink: {
            to: _.isUndefined(onReturnLinkClick) ? '/home' : '#',
            content: translator(returnLinkText),
            onClick: onReturnLinkClick
        },
        enrollButton: {
            content: translator(enrollButtonText),
            trigger: enrollPolicy
        },
        addressLookup: {
            onLoadValues: getAddressesFromTheApi,
            onValueChange: handleValueAddressLookup
        }
    };

    const resolvers = {
        /*resolveCallbackMap: {
            getAddressesFromTheApi,
            handleValueAddressLookup
        },*/
        resolveClassNameMap: styles,
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={formData}
            overrideProps={overrideProps}
            onValueChange={writeValue}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

EnrollmentComponent.propTypes = {
    enrollmentType: PropTypes.string.isRequired,
    onSuccessEnrollment: PropTypes.func,
    onReturnLinkClick: PropTypes.func,
    returnLinkText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    }),
    enrollButtonText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    }),
    informationText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    }).isRequired,
    informationAdditionalText: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    }).isRequired
};

EnrollmentComponent.defaultProps = {
    onSuccessEnrollment: undefined,
    onReturnLinkClick: undefined,
    returnLinkText: messages.returnToHomePage,
    enrollButtonText: messages.enroll
};

export default EnrollmentComponent;
